import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Eraser, FileText, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "./common";
import { api, formatApiError, openInterventionPdf } from "@/lib/api";
import { fmtDateTime } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

export const usePad = (resetKey) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    if (!resetKey) return;
    const t = setTimeout(() => {
      const c = ref.current;
      if (!c) return;
      const r = c.getBoundingClientRect();
      c.width = r.width * 2;
      c.height = r.height * 2;
      const ctx = c.getContext("2d");
      ctx.scale(2, 2);
      ctx.lineWidth = 2.4;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0A1630";
      setEmpty(true);
    }, 150);
    return () => clearTimeout(t);
  }, [resetKey]);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };
  const down = (e) => {
    e.preventDefault();
    ref.current.setPointerCapture(e.pointerId);
    drawing.current = true;
    const ctx = ref.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(...pos(e));
  };
  const move = (e) => {
    if (!drawing.current) return;
    const ctx = ref.current.getContext("2d");
    ctx.lineTo(...pos(e));
    ctx.stroke();
    setEmpty(false);
  };
  const up = () => {
    drawing.current = false;
  };
  const clear = () => {
    const c = ref.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setEmpty(true);
  };
  return { ref, empty, clear, handlers: { onPointerDown: down, onPointerMove: move, onPointerUp: up, onPointerLeave: up } };
};

const ROLES = {
  technician: { title: "Signature du technicien", signed: (i) => i?.tech_signed_at, who: (i) => i?.tech_signature_name },
  client: { title: "Signature du client", signed: (i) => i?.signed_at, who: (i) => i?.signature_name },
  depot: { title: "Signature de dépôt", signed: (i) => i?.depot_signed_at, who: (i) => i?.depot_signature_name },
  restitution: { title: "Signature de restitution", signed: (i) => i?.restitution_signed_at, who: (i) => i?.restitution_signature_name },
};
const LABELS = { technician: "Technicien", client: "Client", depot: "Dépôt", restitution: "Restitution" };

const SignedState = ({ role, intervention, onRedo }) => (
  <div className="space-y-3 rounded-lg border border-emerald-400/30 bg-emerald-400/5 p-4" data-testid={`signature-${role}-signed`}>
    <p className="text-sm text-emerald-200">Signée par <strong>{ROLES[role].who(intervention)}</strong> le {fmtDateTime(ROLES[role].signed(intervention))}.</p>
    <p className="text-xs text-slate-400">La signature validée est conservée. La remplacer est une action volontaire, enregistrée dans l'historique.</p>
    <Button variant="secondary" onClick={onRedo} data-testid={`signature-${role}-redo`}><RotateCcw className="mr-1 h-4 w-4" />Refaire la signature</Button>
  </div>
);

export const SignatureDialog = ({ intervention, onOpenChange, onSaved, roles = ["technician", "client"], title = "Signatures du bon d'intervention", onPdf, startReplace = false }) => {
  const open = !!intervention;
  const { user } = useAuth();
  const [role, setRole] = useState(roles[0]);
  const [replace, setReplace] = useState(false);
  const [reason, setReason] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const signed = !!ROLES[role].signed(intervention);
  const pad = usePad(open && (!signed || replace) ? `${intervention?.id}-${role}-${replace}` : null);

  useEffect(() => {
    if (!open) return;
    if (roles.length === 1 || !roles.includes("client")) setRole(roles.find((r) => !ROLES[r].signed(intervention)) || roles[0]);
    else setRole(intervention.tech_signed_at && !intervention.signed_at ? "client" : "technician");
  }, [open, intervention]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setReplace(startReplace && !!ROLES[role].signed(intervention));
    setReason(startReplace ? "Document modifié depuis la signature" : "");
    setName(role !== "technician" ? intervention?.client_name || "" : user?.name || user?.email || "");
  }, [role, intervention, user, startReplace]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/interventions/${intervention.id}/signature`, { role, name, image: pad.ref.current.toDataURL("image/png"), replace, reason });
      toast.success(`${ROLES[role].title} enregistrée`);
      const id = intervention.id;
      onOpenChange(false);
      onSaved?.();
      toast(onPdf ? "Bon de dépôt mis à jour" : "Bon d'intervention mis à jour", { action: { label: "Ouvrir le PDF", onClick: () => (onPdf ? onPdf() : openInterventionPdf(id)) } });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="signature-dialog" className="max-h-[95vh] overflow-y-auto bg-[#0F1F3D] border-cyan-400/20 sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{title}</DialogTitle>
          <DialogDescription className="text-slate-400">{intervention?.title} — signez à la souris, au doigt ou au stylet.</DialogDescription>
        </DialogHeader>
        <Tabs value={role} onValueChange={setRole}>
          {roles.length > 1 && <TabsList className="grid w-full grid-cols-2">
            {roles.map((r) => <TabsTrigger key={r} value={r} data-testid={`signature-tab-${r}`}>{LABELS[r]}{ROLES[r].signed(intervention) ? " ✓" : ""}</TabsTrigger>)}
          </TabsList>}
        </Tabs>
        {signed && !replace ? (
          <SignedState role={role} intervention={intervention} onRedo={() => setReplace(true)} />
        ) : (
          <>
            {role !== "technician" ? (
              <Field label="Nom du client signataire *"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="signature-name-input" /></Field>
            ) : (
              <p className="text-sm text-slate-300" data-testid="signature-technician-name">Technicien : <strong className="text-white">{name}</strong> (compte connecté)</p>
            )}
            {replace && <Field label="Motif du remplacement"><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. signature illisible" data-testid="signature-reason-input" /></Field>}
            <canvas ref={pad.ref} {...pad.handlers} data-testid="signature-canvas" className="h-56 w-full touch-none rounded-lg border-2 border-dashed border-cyan-400/40 bg-white sm:h-64" />
          </>
        )}
        <div className="flex flex-wrap justify-between gap-2">
          {signed && !replace ? <span /> : <Button variant="ghost" onClick={pad.clear} data-testid="signature-clear-button"><Eraser className="mr-1 h-4 w-4" />Effacer</Button>}
          <div className="flex gap-2">
            {(intervention?.signed_at || intervention?.tech_signed_at) && (
              <Button variant="secondary" onClick={() => openInterventionPdf(intervention.id)} data-testid="signature-open-pdf"><FileText className="mr-1 h-4 w-4" />PDF actuel</Button>
            )}
            {(!signed || replace) && (
              <Button onClick={save} disabled={pad.empty || !name.trim() || saving} className="dt-glow" data-testid="signature-save-button">
                {saving ? "Enregistrement…" : replace ? "Remplacer la signature" : `Valider la ${ROLES[role].title.toLowerCase()}`}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
